import React from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  RedAsterix,
  VStack,
} from '@wegroup/design-system';
import { Controller, useFormContext } from 'react-hook-form';
import InsuranceProductSearch, {
  RiskType,
} from '../../../../shared/InsuranceProductSearch';
import AddInsuranceModalInsuranceFormPremiumFields from '../insurance-form/AddInsuranceModalInsuranceFormPremiumFields';
import { CoverageFormValues } from '../../../../../types/Form';
import { useTranslation } from 'react-i18next';
import useCreationFlowReport from '../../../../../hooks/useCreationFlowReport';
import { PartyType } from '../../../../../../../../../../types/SMT';
import { validate } from 'uuid';
import AddInsuranceModalInsuranceFormProvision from './AddInsuranceModalInsuranceFormProvision';
import useIsNLplan from '../../../../../../../../../common/src/hooks/useIsNLPlan';
import { InsurancePolicyType } from '../../../../../../../types/AdvisoryReport';
import AddInsuranceModalInsuranceFormInsuranceTax from './AddInsuranceModalInsuranceFormInsuranceTax';
import { useParty } from '../../../../../../../../../parties/src/features/party-company-extended-detail/api/getParty';
import { Language } from '../../../../../../../../../../types/Common';

interface Props {
  partyType?: PartyType;
  /** When providing a riskId it will be use for filtering the product selection */
  riskId?: string;
  riskType?: RiskType;
  type: InsurancePolicyType;
}

const AddInsuranceModalCoverageFormFields: React.FC<Props> = ({
  partyType,
  riskId,
  type,
}) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CoverageFormValues>();
  const { isNL } = useIsNLplan();
  const { data } = useCreationFlowReport();
  const { data: partyData } = useParty(data?.party.id);
  const insuranceId = watch('insurance_product_id');

  const getRiskType = (): RiskType | undefined => {
    if (!riskId) return;
    if (riskId === data?.party.display_name) {
      return 'PARTY';
    }
    if (partyData?.managements?.some(({ name }) => name === riskId)) {
      return 'MANAGEMENT';
    }
    if (riskId === 'staff') {
      return 'STAFF';
    }
    return 'RISK-OBJECT';
  };

  const riskType = getRiskType();

  return (
    <>
      <VStack spacing="4" align="stretch" key={insuranceId}>
        <Controller
          name="insurance_product_id"
          control={control}
          render={({ field, fieldState: { error: _error } }) => {
            return (
              <FormControl isInvalid={!!_error}>
                <FormLabel>
                  {t(
                    'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_COVERAGE',
                  )}
                </FormLabel>
                <InsuranceProductSearch
                  riskId={
                    riskType && riskType !== 'RISK-OBJECT' && riskId
                      ? data?.party.id
                      : validate(riskId || '')
                      ? riskId
                      : undefined
                  }
                  riskType={riskType}
                  language={data?.language?.toLowerCase() as Language}
                  partyType={
                    partyType || (data?.party.type as PartyType) || 'CUSTOMER'
                  }
                  {...field}
                  onChange={(value, label, product) => {
                    if (label) {
                      setValue('name', label);
                    }
                    const commission =
                      product?.risk_domain?.default_commission_percentage;
                    if (commission != null) {
                      setValue('commission_percentage', commission * 100);
                    }
                    field.onChange(value);
                  }}
                />
                {_error?.message && (
                  <FormErrorMessage>{t(_error.message)}</FormErrorMessage>
                )}
              </FormControl>
            );
          }}
        />
        <FormControl isInvalid={!!errors.name}>
          <FormLabel>
            {t('DISPLAY-NAME')} <RedAsterix />
          </FormLabel>
          <Input {...register('name')} />
          {errors?.name?.message && (
            <FormErrorMessage>{t(errors?.name?.message)}</FormErrorMessage>
          )}
        </FormControl>
      </VStack>
      <AddInsuranceModalInsuranceFormPremiumFields showAllFields />
      {isNL &&
        type === 'OFFER' &&
        (partyType || (data?.party.type as PartyType) === 'CUSTOMER') && (
          <InsuranceFormAdditionalFields renderKey={insuranceId} />
        )}
    </>
  );
};

const InsuranceFormAdditionalFields: React.FC<{ renderKey?: string }> = ({
  renderKey,
}) => {
  return (
    <>
      <AddInsuranceModalInsuranceFormInsuranceTax />
      <AddInsuranceModalInsuranceFormProvision key={renderKey} />
    </>
  );
};

export default AddInsuranceModalCoverageFormFields;
